/* istanbul ignore file */
import React from 'react';
import { batch } from 'react-redux';
import { useCurator } from '@noths/curator-web';
import type { CookieObj } from '@noths/polaris-client-gdpr-compliance';
import { CookieModal, getCookieConsent } from '@noths/polaris-client-gdpr-compliance';
import { GTMWithDataLayer } from '@noths/polaris-client-google-analytics';
import type { DeliveryZoneCode } from '@noths/polaris-client-localisation';
import { parseLocaleSetCookieHeaders } from '@noths/polaris-client-localisation';
import { isContentPreviewEnabled } from '@noths/polaris-client-next-components';
import { PreviewHeader } from '@noths/polaris-client-ribbons-design-system';
import { getMvtStrategyFromRequest } from '@noths/polaris-client-user-configuration';
import { generateRandomString } from '@noths/polaris-client-utils';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';
import { StatusCode } from '@noths/polaris-dev-ts-types';
import type { GetServerSideProps } from 'next';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { parse } from 'url';

import { adaptReviewsToPageProps } from 'src/adapters/storeToPageProps/adaptReviewsToPageProps';
import { MiraklViewBeacon } from 'src/components/atoms/MiraklViewBeacon/MiraklViewBeacon';
import { SupportShowInCMSBookmarklet } from 'src/components/atoms/SupportShowInCMSBookmarklet/SupportShowInCMSBookmarklet';
import { useProductReviewsFetch } from 'src/components/layouts/hooks/useProductReviewsFetch';
import { PDPLayout } from 'src/components/layouts/PDP/PDPLayout';
import { ContactPartnerModalContainer } from 'src/components/molecules/ContactPartnerModal/ContactPartnerModalContainer';
import { PageErrorMessage } from 'src/components/molecules/PageErrorMessage/PageErrorMessage';
import { BasketModalContainer } from 'src/components/organisms/BasketModal/containers/BasketModalContainer';
import { ERROR_PRODUCT_NOT_FOUND } from 'src/constants/errors';
import { isProd } from 'src/environment';
import { getFeatureFlagsWithMatchers } from 'src/featureFlags/getFeatureFlagsWithMatchers';
import { getProductJSONLDSchema } from 'src/JSONLDSchema/getProductJSONLDSchema';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { adaptContentstackContent } from 'src/redux/content/adapters/adaptContentstackContent';
import { actions as contentActions } from 'src/redux/content/slice';
import { actions as deliveryActions } from 'src/redux/delivery/slice';
import { requestDeliveryZoneOptions } from 'src/redux/delivery/thunks';
import { requestProductDetails } from 'src/redux/product/thunks';
import { hasEnoughReviewsForAiSummary } from 'src/redux/reviews/hasEnoughReviewsForAiSummary';
import { requestAiSummary } from 'src/redux/reviews/thunks';
import type { AppThunkDispatch } from 'src/redux/store';
import { getStore } from 'src/redux/store';
import type { CookieConsent } from 'src/redux/user/slice';
import { actions as userActions } from 'src/redux/user/slice';
import { fetchGlobalContent } from 'src/services/contentstack';
import { fetchPageContent } from 'src/services/contentstack/fetchPageContent';
import { getContentstackEnvironment } from 'src/services/contentstack/getContentstackEnvironment';
import type { NextConfig } from 'src/types/nextConfig';
import type { PageProps } from 'src/types/page';
import { getServerSidePageEventObject } from 'src/utils/gaPageEvent';
import { isOutOfStock } from 'src/utils/inventoryStatus';
import { getIsRequestFromExternalReferrer } from 'src/utils/page';
import { logger } from 'src/utils/serverLogger';

const { publicRuntimeConfig, serverRuntimeConfig } = getConfig() as NextConfig;

const { CLEAR_PREVIEW_API_URL, appVersion, version } = publicRuntimeConfig;

const Curator = dynamic(
  () =>
    import('@noths/curator-web').then(async (m) => {
      m.configureCurator({
        env: isProd() ? 'production' : 'qa',
        hideChatToggleForMobileViewport: true,
      });
      return m.Curator;
    }),
  { ssr: false },
);

export const getServerSideProps: GetServerSideProps<PageProps> = async ({ query, req, res }) => {
  const pathname = parse(req.url!).pathname!;
  const domain = publicRuntimeConfig.hosts.domain;
  const canonicalUrl = `https://${domain}${pathname}`;
  const externalReferrer = getIsRequestFromExternalReferrer(req, query);
  const isPreview = isContentPreviewEnabled(req);

  const {
    countryCode: initialCountryCode,
    currency: initialCurrency,
    deliveryZone: initialDeliveryZone,
  } = parseLocaleSetCookieHeaders(res.getHeaders());

  const { _ga: googleAnalyticsVisitorId, country_code, currency, delivery_zone } = req.cookies;
  const cookieConsent: CookieConsent = getCookieConsent(req.cookies as CookieObj);
  const featureFlags = (req && getFeatureFlagsWithMatchers(req)) || {};
  const gaVisitorId = googleAnalyticsVisitorId ?? generateRandomString(10);

  const countryCode = initialCountryCode ?? country_code;
  const currencyCode = (initialCurrency ?? currency) as CurrencyCode;
  const deliveryZone = (initialDeliveryZone ?? delivery_zone) as DeliveryZoneCode;

  const { dispatch, getState } = getStore(
    {
      router: { pathname, query },
    },
    {
      user: {
        bot: getMvtStrategyFromRequest(req) === 'bot',
        countryCode,
        currencyCode,
        gaVisitorId,
      },
    },
  );

  batch(() => {
    dispatch(userActions.setCookieConsent(cookieConsent));
    dispatch(userActions.setDeliveryZone(deliveryZone));
    dispatch(userActions.setFeatureFlags(featureFlags));
    dispatch(userActions.setContentPreview(isPreview));
    dispatch(deliveryActions.setSelectedCountry(countryCode));
    dispatch(deliveryActions.setSelectedZone(deliveryZone));
  });

  // Delivery options depend only on the product, so chain them straight off
  // that fetch rather than awaiting the whole wave first. The 500ms delivery
  // call then overlaps the slower content calls instead of queuing behind
  // them. `code` stays 0 on both the redirect and error paths, so the guard
  // below still skips the call in those cases.
  const productAndDependents = dispatch(
    requestProductDetails({
      cookie: req.headers.cookie,
      healthcheck: req.headers['health-check'] === 'true',
      previewing: !!query.previewing,
      productURI: pathname,
    }),
  ).then(async () => {
    const { product } = getState();

    // code stays 0 on the redirect and error paths, so this guard also keeps
    // the delivery and reviews calls off those paths entirely.
    if (!product.code) {
      return;
    }

    const dependentRequests: Promise<unknown>[] = [];

    // The AI summary is the only reviews call made during SSR; the rating
    // breakdown and product insights are fetched client-side through the
    // aggregate internal API route. Skipped where the service would refuse
    // anyway, leaving the store's null. Non-critical: the thunk settles its
    // field to null on failure and never rejects, so the catch is a backstop
    // only.
    if (hasEnoughReviewsForAiSummary(product)) {
      dependentRequests.push(
        (dispatch as AppThunkDispatch)(requestAiSummary({ productCode: product.code })).catch(
          () => null,
        ),
      );
    }

    if (!isOutOfStock(product.inventoryStatus) && product.purchasable) {
      dependentRequests.push(
        (dispatch as AppThunkDispatch)(
          requestDeliveryZoneOptions({
            productCode: product.code,
            productPrice: product.price,
          }),
        ),
      );
    }

    await Promise.all(dependentRequests);
  });

  const [, [siteChromeContent], pageContent] = await Promise.all([
    productAndDependents,
    fetchGlobalContent({ featureFlags }),
    fetchPageContent(getContentstackEnvironment(isPreview)).catch(() => null),
  ]);

  const campaignIds = getState().product.campaignIds ?? [];

  if (pageContent) {
    const contentState = adaptContentstackContent(pageContent, campaignIds);

    dispatch(contentActions.setContent(contentState));
  }

  // A product in campaigns no authored label lists renders nothing, so this log is the only
  // trace of the gap; a Datadog log-based metric on it is the miss counter.
  if (campaignIds.length > 0 && !getState().content.promoLabel) {
    logger.warn('promo_label_miss', { campaignIds });
  }

  // Check for redirects/errors
  const { product: initialProduct } = getState();

  if (initialProduct.redirectUrl) {
    return {
      redirect: {
        destination: initialProduct.redirectUrl,
        statusCode: StatusCode.MovedPermanently,
      },
    };
  }

  if (initialProduct.error) {
    res.statusCode =
      initialProduct.error.name === ERROR_PRODUCT_NOT_FOUND
        ? StatusCode.NotFound
        : StatusCode.InternalServerError;

    if (res.statusCode === StatusCode.InternalServerError) {
      res.setHeader('Retry-After', serverRuntimeConfig.retryAfterDuration);
    }
  }

  // Get final state after all async operations, including delivery options
  const state: ReduxApplicationState = getState();
  const { basket, content, delivery, product, productMediaCarousel, reviews, user } = state;
  const props: PageProps = {
    canonicalUrl,
    siteChromeContent,
    externalReferrer,
    updatedReduxState: {
      // Cherry pick the state to be updated on the client. Partner content and insights are
      // fetched client-side, so the partner slice is left to hydrate from its initial state.
      basket,
      content,
      delivery,
      product,
      productMediaCarousel,
      reviews: adaptReviewsToPageProps(reviews),
      user,
    },
  };

  return { props };
};

export default (props: PageProps) => {
  const { canonicalUrl, externalReferrer, updatedReduxState } = props;
  const { basket, delivery, product, user } = updatedReduxState;
  const baseUrl = canonicalUrl ? new URL(canonicalUrl).origin : '';
  const productJSONLDSchema = getProductJSONLDSchema(product, delivery, baseUrl);
  const pageEventData =
    !!updatedReduxState?.product && !!updatedReduxState?.basket
      ? getServerSidePageEventObject({
          // Partner insights load client-side and are not available at initial render.
          partnerAverageProductRating: undefined,
          version,
          appVersion,
          externalReferrer,
          layoutType: 'standard',
          optionsCount: basket.options.length,
          productState: product,
          requestIsFromBot: updatedReduxState.user.bot,
          selectedOptionValues: basket.selectedOptionValues,
        })
      : {};

  useProductReviewsFetch(product.code);

  const { handleToggle, isCuratorOpen, redirectToPDP } = useCurator();

  return (
    <>
      <Head>
        <title>{`${product.title} By ${product.partner.name}`}</title>
        <meta content={product.metaDescription} name="description" />
        {!product.metaRobotsIndexable && <meta content="noindex,follow" name="robots" />}
        <link href={canonicalUrl} rel="canonical" />
        {productJSONLDSchema && (
          <script
            dangerouslySetInnerHTML={{ __html: JSON.stringify(productJSONLDSchema) }}
            type="application/ld+json"
          />
        )}
        <CookieModal useTestScript={!isProd()} />
      </Head>
      <GTMWithDataLayer pageEventData={pageEventData} />
      {user.contentPreview && <PreviewHeader href={CLEAR_PREVIEW_API_URL} />}
      {product.error ? (
        <PageErrorMessage pageNotFound={product.error.name === ERROR_PRODUCT_NOT_FOUND} />
      ) : (
        <>
          <PDPLayout />
          <BasketModalContainer />
          <ContactPartnerModalContainer />
          <SupportShowInCMSBookmarklet productCode={product.code} />
          <MiraklViewBeacon productCode={product.code} />
        </>
      )}
      <Curator
        isOpen={isCuratorOpen}
        onProductClick={redirectToPDP}
        onToggle={handleToggle}
        showFavourties
      />
    </>
  );
};
